import {
  BOARD_COLS,
  BOARD_MAX_ITEMS,
  type Board,
  COMMAND_BOX_ID,
  type WidgetInstance,
} from "@kourai/api/home-board";
import type { Layout, LayoutItem } from "react-grid-layout";
import { verticalCompactor } from "react-grid-layout/core";
import { WIDGET_SIZES, type WidgetSize } from "./widget";

/**
 * Board edits as pure functions (ADMIN-258). The grid library only ever sees `toGrid(board)` and
 * hands back positions through `fromGrid`; everything else (adding, keyboard moves, resizing)
 * happens here on our own format, so it's testable without a DOM and the library stays swappable.
 *
 * The command box is item `bar` at row 0 of the stored board. The grid shows the other widgets
 * from its own row 0, so rows are shifted by one at this edge.
 */

const isBar = (i: WidgetInstance) => i.id === COMMAND_BOX_ID;

/** The board's widgets as the grid library's layout (command box left out, rows shifted up). */
export function toGrid(board: Board): Layout {
  return board.items
    .filter((i) => !isBar(i))
    .map((i) => ({ i: i.id, x: i.x, y: Math.max(0, i.y - 1), w: i.w, h: i.h }));
}

/** Positions from the grid back onto the board. Ids the grid doesn't know are kept as they were. */
export function fromGrid(board: Board, layout: readonly LayoutItem[]): Board {
  const byId = new Map(layout.map((l) => [l.i, l]));
  return {
    ...board,
    items: board.items.map((item) => {
      const l = byId.get(item.id);
      return l && !isBar(item) ? { ...item, x: l.x, y: l.y + 1, w: l.w, h: l.h } : item;
    }),
  };
}

/** True when the two boards place every widget the same way (to skip no-op saves). */
export function sameLayout(a: Board, b: Board) {
  if (a.items.length !== b.items.length) return false;
  return a.items.every((x, n) => {
    const y = b.items[n];
    return (
      y &&
      x.id === y.id &&
      x.x === y.x &&
      x.y === y.y &&
      x.w === y.w &&
      x.h === y.h &&
      JSON.stringify(x.config) === JSON.stringify(y.config)
    );
  });
}

/** Compacts upward and resolves overlaps, like the grid does after a drag. */
function compact(board: Board): Board {
  return fromGrid(board, verticalCompactor.compact(toGrid(board), BOARD_COLS));
}

/**
 * The first free spot for a w×h tile, scanning rows top to bottom, then columns left to right:
 * a new widget fills a gap next to Recent before going under it.
 *
 * @internal exported for tests
 */
export function firstFit(layout: Layout, w: number, h: number): { x: number; y: number } {
  const taken = (x: number, y: number) =>
    layout.some((l) => x < l.x + l.w && x + w > l.x && y < l.y + l.h && y + h > l.y);
  for (let y = 0; y < 500; y++)
    for (let x = 0; x + w <= BOARD_COLS; x++) if (!taken(x, y)) return { x, y };
  return { x: 0, y: layout.reduce((m, l) => Math.max(m, l.y + l.h), 0) };
}

/** Adds an instance where it first fits. No-op once the board is full. */
export function addWidget(board: Board, instance: WidgetInstance): Board {
  if (board.items.length >= BOARD_MAX_ITEMS) return board;
  const { x, y } = firstFit(toGrid(board), instance.w, instance.h);
  return { ...board, items: [...board.items, { ...instance, x, y: y + 1 }] };
}

export function removeWidget(board: Board, id: string): Board {
  if (id === COMMAND_BOX_ID) return board;
  return compact({ ...board, items: board.items.filter((i) => i.id !== id) });
}

/**
 * Compacts with one tile held where it was put: the others move out of its way (down), then
 * everything settles upward. This is how an edit "wins" over its neighbours.
 */
function compactAround(board: Board, id: string): Board {
  const held = toGrid(board).map((l) => (l.i === id ? { ...l, static: true } : l));
  const settled = verticalCompactor
    .compact(held, BOARD_COLS)
    .map(({ static: _held, ...l }) => l as LayoutItem);
  return fromGrid(board, verticalCompactor.compact(settled, BOARD_COLS));
}

/** Puts a removed instance back where it was (Undo); the others make room. */
export function restoreWidget(board: Board, instance: WidgetInstance): Board {
  if (board.items.some((i) => i.id === instance.id)) return board;
  return compactAround({ ...board, items: [...board.items, instance] }, instance.id);
}

/** Resizes to a preset size, keeping the tile inside the grid. */
export function resizeWidget(board: Board, id: string, size: WidgetSize): Board {
  const { w, h } = WIDGET_SIZES[size];
  const target = board.items.find((i) => i.id === id);
  if (!target || isBar(target)) return board;
  const items = board.items.map((i) =>
    i.id === id ? { ...i, w, h, x: Math.min(i.x, BOARD_COLS - w) } : i,
  );
  return compactAround({ ...board, items }, id);
}

/**
 * Moves a tile one step (keyboard: arrow keys in Customize). Up and down swap places with the
 * neighbouring tile, since a one row nudge would just float back up.
 */
export function moveWidget(board: Board, id: string, dx: number, dy: number): Board {
  const grid = toGrid(board);
  const mine = grid.find((l) => l.i === id);
  if (!mine) return board;
  const x = Math.max(0, Math.min(BOARD_COLS - mine.w, mine.x + dx));
  let y = mine.y;
  if (dy < 0) y = Math.max(0, mine.y - 1);
  if (dy > 0) {
    // The compactor never lets a tile jump over a held one, so moving down past the tile below
    // is done as that tile moving up (a swap).
    const under = grid.find(
      (l) => l.i !== id && l.y === mine.y + mine.h && l.x < x + mine.w && l.x + l.w > x,
    );
    if (under && x === mine.x) return moveWidget(board, under.i, 0, -1);
    y = mine.y + 1;
  }
  if (x === mine.x && y === mine.y) return board;
  const items = board.items.map((i) => (i.id === id ? { ...i, x, y: y + 1 } : i));
  return compactAround({ ...board, items }, id);
}

/** The next larger (or smaller) of a widget's allowed sizes, for Shift+arrow resizing. */
export function stepSize(
  allowed: readonly WidgetSize[],
  current: Pick<WidgetInstance, "w" | "h">,
  dir: 1 | -1,
): WidgetSize | null {
  const area = (s: WidgetSize) => WIDGET_SIZES[s].w * WIDGET_SIZES[s].h * 10 + WIDGET_SIZES[s].w;
  const sorted = [...allowed].sort((a, b) => area(a) - area(b));
  const now = current.w * current.h * 10 + current.w;
  const pick =
    dir > 0 ? sorted.find((s) => area(s) > now) : sorted.reverse().find((s) => area(s) < now);
  return pick ?? null;
}

/** Widgets in reading order (top to bottom, then left to right): the one column view. */
export function readingOrder(board: Board): WidgetInstance[] {
  return board.items.filter((i) => !isBar(i)).sort((a, b) => a.y - b.y || a.x - b.x);
}

/**
 * Whether the board has grown past today's home (command box and Recent). The page stays 760px
 * until then and widens to about 1120px after (Omar's call, 4 Nov 2026).
 */
export function isWideBoard(board: Board) {
  return board.items.some((i) => !isBar(i) && i.type !== "kourai.recent");
}

/** A short random id for a new widget instance. */
export function widgetId() {
  const bytes = crypto.getRandomValues(new Uint8Array(6));
  return `w${[...bytes].map((b) => b.toString(36).padStart(2, "0")).join("")}`.slice(0, 12);
}

/**
 * A starter board made yours: every widget but the command box gets a new id, so two people (or
 * two picks) never share widget content by accident.
 */
export function fromStarter(board: Board, newId: () => string = widgetId): Board {
  return {
    ...structuredClone(board),
    items: board.items.map((i) =>
      i.id === COMMAND_BOX_ID ? structuredClone(i) : { ...structuredClone(i), id: newId() },
    ),
  };
}
